/**
 * Envoi d'un document demandé par un formulaire de billetterie ou de boutique
 * (question « Fichier » : certificat médical, pièce d'identité…).
 *
 * Karim 2026-09-29 (« atelier-musique ») : la question s'affichait sans aucun
 * moyen d'envoyer le fichier. Le document part DÈS qu'il est choisi, vers
 * `POST /api/public/events/:slug/documents` (ou `/shops/:slug/documents`) ; la
 * réponse `{ file_id, file_name }` devient la réponse à la question, et le
 * serveur la re-vérifie à la commande.
 *
 * Mêmes règles que les justificatifs d'adhésion : PDF, JPG, PNG, WebP, 10 Mo.
 */
import { extractApiError } from "@donoor/utils/error";

/** Réponse à une question « Fichier », telle qu'envoyée avec la commande. */
export interface DocumentEnvoye {
  file_id: string;
  file_name: string;
}

interface ReponseEnvoi {
  success?: boolean;
  data?: DocumentEnvoye;
  message?: string;
}

export const TAILLE_DOCUMENT_MAX = 10 * 1024 * 1024;
export const ACCEPT_DOCUMENT = ".pdf,.jpg,.jpeg,.png,.webp";
const EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".webp"];
const TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
/**
 * Traduction hors composant : via l'instance i18n de Nuxt, résolue à chaque
 * appel → suit la langue de la page (cf. docs/architecture/i18n.md).
 */
function t(cle: string): string {
  return useNuxtApp().$i18n.t(cle);
}

/** Ce qui empêche d'envoyer ce fichier, ou `null` s'il convient. */
export function problemeDuDocument(fichier: File): string | null {
  if (fichier.size > TAILLE_DOCUMENT_MAX) {
    return t("association.champsFormulaire.fichierTropVolumineux");
  }
  const extension = `.${(fichier.name.split(".").pop() || "").toLowerCase()}`;
  if (!TYPES.includes(fichier.type) && !EXTENSIONS.includes(extension)) {
    return t("association.champsFormulaire.typeNonAutorise");
  }
  return null;
}

/** La valeur est-elle un document envoyé (`{ file_id, file_name }`) ? */
export function estDocumentEnvoye(valeur: unknown): valeur is DocumentEnvoye {
  return (
    !!valeur &&
    typeof valeur === "object" &&
    !Array.isArray(valeur) &&
    typeof (valeur as { file_id?: unknown }).file_id === "string"
  );
}

/**
 * Envoie le fichier à `url`. Rejette avec un message à montrer tel quel sous
 * la question (format refusé, trop lourd, serveur indisponible…).
 */
export async function envoyerDocumentFormulaire(url: string, fichier: File): Promise<DocumentEnvoye> {
  const probleme = problemeDuDocument(fichier);
  if (probleme) throw new Error(probleme);

  const corps = new FormData();
  corps.append("file", fichier);
  let reponse: ReponseEnvoi | null;
  try {
    // Type explicite : sans lui, TypeScript explore toutes les routes Nitro.
    reponse = await $fetch<ReponseEnvoi>(url, { method: "POST", body: corps });
  } catch (erreur) {
    throw new Error(extractApiError(erreur, { technicalFallback: t("association.champsFormulaire.envoiEchec") }).message);
  }
  if (!reponse?.success || !estDocumentEnvoye(reponse.data)) {
    throw new Error(reponse?.message || t("association.champsFormulaire.envoiEchec"));
  }
  return { file_id: reponse.data.file_id, file_name: reponse.data.file_name || fichier.name };
}

/**
 * Adresse d'envoi des documents de la page en cours (`/b/:slug`,
 * `/boutique/:slug/…`) : les documents sont rattachés à CET événement ou à
 * CETTE boutique dès l'envoi.
 */
export function useUrlEnvoiDocument(domaine: "events" | "shops") {
  const config = useRuntimeConfig();
  const route = useRoute();
  return computed(() => {
    const slug = String((route.params as Record<string, unknown>).slug ?? "");
    if (!slug) return undefined;
    return `${config.public.apiBaseUrl}/api/public/${domaine}/${encodeURIComponent(slug)}/documents`;
  });
}
